import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../../common/toast/ToastContext";
import AdminPageLayout from "../components/AdminPageLayout";
import {
  fetchMediaVideos,
  uploadMediaVideo,
  replaceMediaVideo,
  updateMediaVideoMeta,
  restoreMediaVideo,
  softDeleteMediaVideo,
  hardDeleteMediaVideo,
  streamMediaVideo,
  fetchMediaVideoUses,
} from "./adminMediaVideoService";
import "../media_images/mediaImage.css";
import "./mediaVideo.css";

const VISIBILITIES = ["PUBLIC", "PROTECTED", "PRIVATE"];
const ACCEPTED = "video/mp4,video/webm,video/ogg,video/quicktime";
const MAX_SIZE = 500 * 1024 * 1024;
const PAGE_SIZE = 20;

const emptyUpload = { name: "", visibility: "PUBLIC", videoUse: "", description: "", courseId: "" };

const visibilityBadgeClass = (v) => {
  if (v === "PUBLIC") return "mi-badge mi-badge-public";
  if (v === "PROTECTED") return "mi-badge mi-badge-protected";
  return "mi-badge mi-badge-private-video";
};

const videoUseBadgeClass = (u) => {
  const map = { COURSE: "course", GENERAL: "general", DRAFT: "draft" };
  return `mi-badge mi-badge-${map[u] || "general"}`;
};

const formatSize = (bytes) => {
  if (!bytes) return "";
  return bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const AdminMediaVideoPage = () => {
  const { showToast } = useToast();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState({ visibility: "", videoUse: "" });
  const [showDeleted, setShowDeleted] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState(emptyUpload);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [videoUses, setVideoUses] = useState([]);
  const [usesLoading, setUsesLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [previewId, setPreviewId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const replaceRefs = useRef({});
  const prevBlobUrl = useRef(null);

  const load = async (p = page, f = filters, deleted = showDeleted) => {
    setLoading(true);
    const res = await fetchMediaVideos({ page: p, size: PAGE_SIZE, ...f, ...(deleted ? { showDeleted: true } : {}) });
    if (res.status === 200) {
      const list = Array.isArray(res.data) ? res.data : res.data.content ?? [];
      setVideos(list);
      setHasMore(Array.isArray(res.data) ? list.length === PAGE_SIZE : !res.data.last);
    } else {
      showToast(res.message || "Failed to load videos", "error");
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(0, filters, showDeleted); setPage(0); }, [filters, showDeleted]);

  useEffect(() => {
    const loadUses = async () => {
      setUsesLoading(true);
      const res = await fetchMediaVideoUses();
      if (res.status === 200) setVideoUses(res.data || []);
      else showToast("Failed to load video uses", "error");
      setUsesLoading(false);
    };
    loadUses();
  }, []);

  useEffect(() => {
    return () => { if (prevBlobUrl.current) URL.revokeObjectURL(prevBlobUrl.current); };
  }, []);

  const handleFilterChange = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  const validateFile = (file) => {
    if (!file) return "No file selected";
    const allowed = ["video/mp4", "video/webm", "video/ogg", "video/quicktime"];
    if (!allowed.includes(file.type)) return "Invalid file type. Allowed: MP4, WebM, OGG, MOV";
    if (file.size > MAX_SIZE) return "File must be under 500 MB";
    return null;
  };

  const handleUpload = async () => {
    console.log("[VideoUpload] handleUpload fired");
    console.log("[VideoUpload] uploadForm:", uploadForm);
    console.log("[VideoUpload] uploadFile:", uploadFile);

    if (!uploadForm.name.trim()) {
      console.warn("[VideoUpload] BLOCKED: name is empty");
      showToast("Name is required", "error"); return;
    }
    const fileErr = validateFile(uploadFile);
    if (fileErr) {
      console.warn("[VideoUpload] BLOCKED: file validation failed —", fileErr);
      showToast(fileErr, "error"); return;
    }
    if (uploadForm.visibility === "PROTECTED" && !uploadForm.courseId) {
      console.warn("[VideoUpload] BLOCKED: PROTECTED visibility requires courseId");
      showToast("Course ID is required for PROTECTED videos", "error"); return;
    }

    setSaving(true);
    setUploadProgress(0);
    console.log("[VideoUpload] calling uploadMediaVideo with file:", uploadFile?.name, "size:", uploadFile?.size);

    const res = await uploadMediaVideo(
      { ...uploadForm, file: uploadFile },
      (e) => {
        if (e.total) {
          const pct = Math.round((e.loaded / e.total) * 100);
          console.log("[VideoUpload] progress:", pct + "%");
          setUploadProgress(pct);
        }
      }
    );

    console.log("[VideoUpload] response:", res.status, res);

    if (res.status === 200 || res.status === 201) {
      showToast("Video uploaded successfully", "success");
      setShowUpload(false); setUploadForm(emptyUpload); setUploadFile(null); setUploadProgress(0);
      load(0, filters); setPage(0);
    } else {
      showToast(res.message || "Upload failed", "error");
    }
    setSaving(false);
  };

  const handleReplace = async (id, e) => {
    const f = e.target.files[0];
    if (!f) return;
    const err = validateFile(f);
    if (err) { showToast(err, "error"); e.target.value = ""; return; }
    const res = await replaceMediaVideo(id, f);
    if (res.status === 200) { showToast("Video replaced", "success"); load(); }
    else showToast(res.message || "Replace failed", "error");
    e.target.value = "";
  };

  const handleSaveMeta = async (id) => {
    setSaving(true);
    const res = await updateMediaVideoMeta(id, editForm);
    if (res.status === 200) { showToast("Updated successfully", "success"); setEditingId(null); load(); }
    else showToast(res.message || "Update failed", "error");
    setSaving(false);
  };

  const handleRestore = async (id) => {
    const res = await restoreMediaVideo(id);
    if (res.status === 200) { showToast("Video restored", "success"); load(); }
    else showToast(res.message || "Restore failed", "error");
  };

  const handleSoftDelete = async (id) => {
    if (!window.confirm("Soft delete this video?")) return;
    const res = await softDeleteMediaVideo(id);
    if (res.status === 200 || res.status === 204) { showToast("Video deleted", "success"); load(); }
    else showToast(res.message || "Delete failed", "error");
  };

  const handleHardDelete = async (id) => {
    if (!window.confirm("This permanently deletes the file from disk and cannot be undone. Continue?")) return;
    if (!window.confirm("Are you absolutely sure? This action is irreversible.")) return;
    const res = await hardDeleteMediaVideo(id);
    if (res.status === 200 || res.status === 204) { showToast("Video permanently deleted", "success"); load(); }
    else showToast(res.message || "Hard delete failed", "error");
  };

  const handlePreview = async (id) => {
    if (previewId === id) { closePreview(); return; }
    setPreviewId(id);
    setPreviewLoading(true);
    if (prevBlobUrl.current) { URL.revokeObjectURL(prevBlobUrl.current); prevBlobUrl.current = null; }
    try {
      const res = await streamMediaVideo(id);
      if (!res.ok) { showToast("Failed to load video preview", "error"); setPreviewId(null); setPreviewLoading(false); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      prevBlobUrl.current = url;
      setPreviewUrl(url);
    } catch {
      showToast("Failed to load video preview", "error");
      setPreviewId(null);
    }
    setPreviewLoading(false);
  };

  const closePreview = () => {
    setPreviewId(null);
    if (prevBlobUrl.current) { URL.revokeObjectURL(prevBlobUrl.current); prevBlobUrl.current = null; }
    setPreviewUrl(null);
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setEditForm({ name: v.name, videoUse: v.videoUse, description: v.description || "", courseId: v.courseId || "" });
  };

  const changePage = (delta) => {
    const next = page + delta;
    setPage(next);
    load(next, filters, showDeleted);
  };

  return (
    <AdminPageLayout
      title="Media Videos"
      subtitle="Manage all media videos used across the platform."
      instruction="Upload videos for courses and general use. PROTECTED videos require a Course ID and are only accessible to enrolled users. Soft delete hides a video; hard delete permanently removes it from disk."
      actions={
        <button className="adm-btn adm-btn-primary" onClick={() => { console.log("[VideoUpload] + Upload Video button clicked"); setShowUpload(true); setUploadForm({ ...emptyUpload, videoUse: videoUses[0] || "" }); setUploadFile(null); setUploadProgress(0); }}>
          + Upload Video
        </button>
      }
    >
      {/* Filters */}
      <div className="mi-filters">
        <select value={filters.visibility} onChange={(e) => handleFilterChange("visibility", e.target.value)}>
          <option value="">All Visibility</option>
          {VISIBILITIES.map((v) => <option key={v}>{v}</option>)}
        </select>
        <select value={filters.videoUse} onChange={(e) => handleFilterChange("videoUse", e.target.value)}>
          <option value="">All Video Use</option>
          {videoUses.map((u) => <option key={u}>{u}</option>)}
        </select>
        <label className="mi-toggle">
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
          Show Deleted
        </label>
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="mi-form-card">
          <h3>Upload New Video</h3>
          <div className="mi-form-grid">
            <div className="form-group">
              <label>Name *</label>
              <input value={uploadForm.name} onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })} placeholder="Video name" />
            </div>
            <div className="form-group">
              <label>Visibility *</label>
              <select value={uploadForm.visibility} onChange={(e) => setUploadForm({ ...uploadForm, visibility: e.target.value })}>
                {VISIBILITIES.map((v) => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Video Use *</label>
              <select
                value={uploadForm.videoUse}
                onChange={(e) => setUploadForm({ ...uploadForm, videoUse: e.target.value })}
                disabled={usesLoading}
              >
                {usesLoading
                  ? <option>Loading…</option>
                  : videoUses.map((u) => <option key={u}>{u}</option>)
                }
              </select>
            </div>
            {uploadForm.visibility === "PROTECTED" && (
              <div className="form-group">
                <label>Course ID *</label>
                <input type="number" value={uploadForm.courseId} onChange={(e) => setUploadForm({ ...uploadForm, courseId: e.target.value })} placeholder="Linked course ID" />
              </div>
            )}
            <div className="form-group">
              <label>Description</label>
              <input value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} placeholder="Optional description" />
            </div>
            <div className="form-group">
              <label>File * (MP4/WebM/OGG/MOV, max 500 MB)</label>
              <input type="file" accept={ACCEPTED} onChange={(e) => {
                const f = e.target.files[0];
                if (f && f.size > MAX_SIZE) { showToast("File exceeds 500 MB limit", "error"); e.target.value = ""; return; }
                setUploadFile(f);
              }} />
              {uploadFile && uploadFile.size > 100 * 1024 * 1024 && (
                <span className="mv-size-warning">⚠ Large file ({formatSize(uploadFile.size)}) — upload may take a while</span>
              )}
            </div>
          </div>
          {saving && uploadProgress > 0 && (
            <div className="mv-progress-bar">
              <div className="mv-progress-fill" style={{ width: `${uploadProgress}%` }} />
              <span>{uploadProgress}%</span>
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="adm-btn adm-btn-primary" onClick={handleUpload} disabled={saving}>
              {saving ? `Uploading… ${uploadProgress}%` : "Upload"}
            </button>
            <button className="adm-btn adm-btn-ghost" onClick={() => { setShowUpload(false); setUploadProgress(0); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <p className="adm-loading">Loading videos…</p>
      ) : videos.length === 0 ? (
        <p className="adm-empty">No videos found.</p>
      ) : (
        <div className="mi-grid">
          {videos.map((vid) => (
            <div key={vid.id} className={`mi-card${vid.deleted ? " mi-card-deleted" : ""}`}>
              <div className="mv-thumb-placeholder">
                <span className="mv-play-icon">▶</span>
                <span className="mv-filename">{vid.originalFilename || vid.name}</span>
              </div>
              <div className="mi-body">
                <span className="mi-name">{vid.name}</span>
                <div className="mi-badges">
                  <span className={videoUseBadgeClass(vid.videoUse)}>{vid.videoUse}</span>
                  <span className={visibilityBadgeClass(vid.visibility)}>{vid.visibility}</span>
                  {vid.courseId && <span className="mi-badge mi-badge-vocabulary">Course #{vid.courseId}</span>}
                </div>
                {vid.description && <span className="mi-alt">{vid.description}</span>}
                <span className="mi-meta">
                  {formatSize(vid.fileSizeBytes)}{vid.fileSizeBytes && vid.createdAt ? " · " : ""}{formatDate(vid.createdAt)}
                </span>
              </div>

              {/* Preview Player */}
              {previewId === vid.id && (
                <div className="mv-preview">
                  {previewLoading ? (
                    <p className="mv-preview-loading">Loading preview…</p>
                  ) : previewUrl ? (
                    <video src={previewUrl} controls autoPlay className="mv-player" />
                  ) : null}
                </div>
              )}

              {/* Inline Edit */}
              {editingId === vid.id && (
                <div className="mi-inline-edit">
                  <div>
                    <label>Name</label>
                    <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                  </div>
                  <div>
                    <label>Video Use</label>
                    <select value={editForm.videoUse} onChange={(e) => setEditForm({ ...editForm, videoUse: e.target.value })}>
                      {videoUses.map((u) => <option key={u}>{u}</option>)}
                    </select>
                  </div>
                  <div>
                    <label>Description</label>
                    <input value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                  </div>
                  <div>
                    <label>Course ID {editForm.visibility === "PROTECTED" ? "*" : "(optional)"}</label>
                    <input type="number" value={editForm.courseId} onChange={(e) => setEditForm({ ...editForm, courseId: e.target.value })} placeholder="Leave blank to unlink" />
                  </div>
                  <div className="mi-inline-edit-actions">
                    <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={() => handleSaveMeta(vid.id)} disabled={saving}>Save</button>
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                </div>
              )}

              <div className="mi-actions">
                {vid.deleted ? (
                  <>
                    <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={() => handleRestore(vid.id)}>Restore</button>
                    <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => handleHardDelete(vid.id)}>Hard Del</button>
                  </>
                ) : (
                  <>
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => handlePreview(vid.id)}>
                      {previewId === vid.id ? "Close" : "Preview"}
                    </button>
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => editingId === vid.id ? setEditingId(null) : startEdit(vid)}>
                      {editingId === vid.id ? "Close" : "Edit Meta"}
                    </button>
                    <input type="file" accept={ACCEPTED} style={{ display: "none" }}
                      ref={(el) => (replaceRefs.current[vid.id] = el)}
                      onChange={(e) => handleReplace(vid.id, e)}
                    />
                    <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => replaceRefs.current[vid.id]?.click()}>Replace</button>
                    <button className="adm-btn adm-btn-warning adm-btn-sm" onClick={() => handleSoftDelete(vid.id)}>Soft Del</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && (videos.length > 0 || page > 0) && (
        <div className="mi-pagination">
          <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => changePage(-1)} disabled={page === 0}>‹ Prev</button>
          <span>Page {page + 1}</span>
          <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => changePage(1)} disabled={!hasMore}>Next ›</button>
        </div>
      )}
    </AdminPageLayout>
  );
};

export default AdminMediaVideoPage;
